import Container from '@ui/container/container';
import Modal from '@ui/modal/modal';
import Text from '@ui/text/text/text';
import Button from '@ui/buttons/button/button';
import { useNavigate } from 'react-router-dom';
import { ICONS } from '@ui/icons/types';
import Icon from '@ui/icons/icon';
import type { Session } from '@/types/session.types';
import WaitingIndicatorOverlay from '../waiting-indicator-overlay';
import type { BrandliveEvent } from '@/types/projects.types';
import { EColorVariablesV2 } from '@/types/theme-packs.types';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';

type DuplicateProjectOrSessionModalProps = {
  isOpen: boolean;
  isDuplicating: boolean;
  mode: 'project' | 'session' | null;
  duplicatedProject?: BrandliveEvent | null;
  duplicatedSession?: Session | null;
  onClose?: () => void;
};

const DuplicateProjectOrSessionModal = ({
  isOpen,
  isDuplicating,
  duplicatedSession,
  duplicatedProject,
  mode,
  onClose,
}: DuplicateProjectOrSessionModalProps) => {
  const navigate = useNavigate();

  if (isDuplicating) {
    return <WaitingIndicatorOverlay display={isDuplicating} />;
  }

  return (
    <Modal
      open={isOpen}
      customSize={{
        width: '440px',
        height: 'auto',
        maxHeight: '260px',
        minHeight: '200px',
        maxWidth: 'calc(100vw - 80px)',
      }}
      contentWrapperClassName="padding-v-24 padding-h-40"
      backgroundColor="var(--gray-900)"
      RenderHeaderContent={() => (
        <Container>
          <div className="flex jc-space-between ai-center margin-b-8">
            <Text tag="h4" color="var(--gray-100)" className="H4">
              Success!
            </Text>
            <button className="clear-button padding-4 position-relative left-14" onClick={onClose}>
              <Icon name={ICONS.CLOSE} size={16} color="var(--gray-400)" />
            </button>
          </div>
          <Text tag="p" color="var(--gray-300)" className="P1">
            Your {mode} was duplicated under:
          </Text>
        </Container>
      )}
      RenderFooterContent={() => (
        <Container className="flex fd-row jc-flex-end ai-center gap-16">
          <Button design="LocalBttnSec" className="L1 padding-v-8 padding-h-16 height-48" onClick={onClose}>
            Back to editing
          </Button>
          <Button
            backgroundColor={getThemeColorCSSVariable()}
            width="135px"
            height="48px"
            className="padding-v-8 padding-h-16"
            hoverOpacity={0.8}
            onClick={() => {
              onClose?.();
              if (mode === 'session' && duplicatedSession?.uuid) {
                // relative redirect for new sessions within the same project
                navigate(`../${duplicatedSession.uuid}`, {
                  relative: 'path',
                });
              }

              if (mode === 'project' && duplicatedProject?.uuid) {
                // full page load for new projects to fully refresh data
                window.location.pathname = `/project/edit/${duplicatedProject.uuid}`;
              }
            }}
          >
            <Text
              color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
              className="L1"
            >
              View duplicate
            </Text>
          </Button>
        </Container>
      )}
    >
      <Container className="margin-v-16 overflow-auto flex-column gap-16">
        <Container background="var(--gray-700)" borderRadius="12px" height="64px" className="flex ai-center">
          <Text clamp={1} className="H4 padding-16 flex-1 text-ellipsis">
            {mode === 'session' ? duplicatedSession?.title.base || '' : duplicatedProject?.name}
          </Text>
        </Container>
      </Container>
    </Modal>
  );
};

export default DuplicateProjectOrSessionModal;
